/**
 * Resolves one favorite team id to a plate that links to that team's card at
 * `/scores?teamId=`. A hook cannot be called in a loop, so one instance of
 * this component resolves one team; mirrors
 * `apps/mobile/src/accountHome/FavoriteTeamPlate.tsx`'s split.
 *
 * The search is an object, not a merge over `prev`, so no `/account` key
 * rides into `/scores`. The skeleton is not a link: there is no team name to
 * announce until the read lands.
 */
import { useTeamGet } from "@sportsmet/sports-sdk/private";
import { cn, FOCUS_RING, NEUTRAL_SURFACE_FRAME, Skeleton } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

export type typeAccountTeamPlateProps = {
  teamId: string;
};

export const AccountTeamPlate = (
  props: typeAccountTeamPlateProps,
): ReactNode => {
  const team = useTeamGet({ teamId: props.teamId });
  const row = team.data?.data?.team;

  if (team.isPending || row == null) {
    return (
      <Skeleton
        shape="circle"
        data-testid={`account-team-loading-${props.teamId}`}
      />
    );
  }

  return (
    <Link
      to="/scores"
      search={{ teamId: props.teamId }}
      data-testid={`account-team-${props.teamId}`}
      className={cn(
        "flex min-h-touch-target flex-col items-center gap-1 rounded-sm text-ink no-underline",
        FOCUS_RING,
      )}
    >
      <span
        aria-hidden="true"
        data-testid="account-team-mark"
        className={cn(
          "flex h-12 w-12 items-center justify-center rounded-full bg-surface-elevated text-caption text-ink",
          NEUTRAL_SURFACE_FRAME,
        )}
      >
        {row.abbreviation}
      </span>
      <span className="text-caption text-ink-secondary">{row.name}</span>
    </Link>
  );
};
